import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import type { DayData } from 'voyager';
import { useRoomCalendarAvailabilityForRange } from '@features/rooms';
import {
  AvailabilityRange,
  initialAvailabilityRange,
  monthStart,
  rangeFor,
} from './availabilityRange';
import { toDayDataMap } from './toDayDataMap';

const DATE_FORMAT = 'YYYY-MM-DD';

type Params = {
  adults: number;
  children: number;
  initialMonth?: string;
  enabled?: boolean;
};

export function useJourneyPickerAvailability({
  adults,
  children,
  initialMonth,
  enabled = true,
}: Params) {
  const [range, setRange] = useState<AvailabilityRange>(() =>
    initialAvailabilityRange(initialMonth)
  );

  useEffect(() => {
    if (!initialMonth) return;
    const target = monthStart(initialMonth);
    setRange((current) =>
      target.isSame(current.start, 'month') ? current : initialAvailabilityRange(initialMonth)
    );
  }, [initialMonth]);

  const startDate = range.start.format(DATE_FORMAT);
  const endDate = range.end.format(DATE_FORMAT);

  const { data: availabilityRecords, isFetching } = useRoomCalendarAvailabilityForRange(
    {
      startDate,
      endDate,
      adults,
      children,
    },
    enabled
  );

  const merged = useRef<{ guests: string; days: Map<string, DayData> }>({
    guests: '',
    days: new Map(),
  });

  const availability = useMemo(() => {
    const guests = `${adults}-${children}`;
    const previous = merged.current.guests === guests ? merged.current.days : new Map();
    const days = new Map([...previous, ...toDayDataMap(availabilityRecords ?? [])]);
    merged.current = { guests, days };
    return days;
  }, [availabilityRecords, adults, children]);

  const handleMonthChange = useCallback((firstVisibleMonth: Date, lastVisibleMonth: Date) => {
    const start = moment(firstVisibleMonth).startOf('month');
    const last = moment(lastVisibleMonth).startOf('month');
    const next = last.isBefore(start) ? rangeFor(start) : { start, end: last.endOf('month') };

    setRange((current) =>
      next.start.isSame(current.start, 'month') && next.end.isSame(current.end, 'month')
        ? current
        : next
    );
  }, []);

  return {
    availability,
    isLoadingAvailability: isFetching,
    onMonthChange: handleMonthChange,
  };
}
